import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Explainer,
  FormGroup,
  Paragraph,
  TextInput,
  InputButton
} from '@sopranodesign/pulse-components';
import Icon from '@sopranodesign/pulse-icons';
import { useMutation } from '@sopranodesign/pulse-hooks';

import { connectSharePoint } from '../../api';

const RegistrationModal = ({ state }) => {
  const [formData, setFormData] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: '',
    showClientSecret: false
  });

  const [errorMessage, setErrorMessage] = useState(null);
  const [fadeError, setFadeError] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const toggleClientSecretVisibility = () => {
    setFormData((prev) => ({ ...prev, showClientSecret: !prev.showClientSecret }));
  };

  const isFormValid = useMemo(() => {
    return formData.tenantId && formData.clientId && formData.clientSecret;
  }, [formData]);

  const mutation = useMutation({
    mutationFn: connectSharePoint,
    onSuccess: () => {
      setErrorMessage(null);
      state.setActiveModal('addSite');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || 'Connection failed.';
      setErrorMessage(message);
      setFadeError(false);
      setTimeout(() => {
        setFadeError(true);
        setTimeout(() => setErrorMessage(null), 1000);
      }, 10000);
    }
  });

  const handleConfirm = () => {
    mutation.mutate({
      tenant_id: formData.tenantId,
      client_id: formData.clientId,
      client_secret: formData.clientSecret
    });
  };

  return (
    <Modal
      header="Connect To SharePoint"
      handleCancel={() => state.setActiveModal(null)}
      handleConfirm={handleConfirm}
      disableConfirmButton={!isFormValid || mutation.isPending}
      confirmText="Connect"
      cancelText="Cancel"
      body={
        <>
          {errorMessage && (
            <Explainer type="danger" mb hideLabel fade={fadeError}>
              <Paragraph sm danger>{errorMessage}</Paragraph>
            </Explainer>
          )}
          <FormGroup>
            <Label htmlFor="tenantId">Tenant ID</Label>
            <TextInput
              id="tenantId"
              placeholder="Please enter your tenant id."
              value={formData.tenantId}
              onChange={handleChange('tenantId')}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="clientId">Client ID</Label>
            <TextInput
              id="clientId"
              placeholder="Please enter your client id."
              value={formData.clientId}
              disabled={mutation.isPending}
              onChange={handleChange('clientId')}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="clientSecret">Client Secret</Label>
            <Flex relative>
              <TextInput
                id="clientSecret"
                type={formData.showClientSecret ? 'text' : 'password'}
                placeholder="Please enter your client secret."
                value={formData.clientSecret}
                disabled={mutation.isPending}
                onChange={handleChange('clientSecret')}
              />
              <InputButton type="button" onClick={toggleClientSecretVisibility}>
                <Icon
                  size="1.25rem"
                  width="1.25rem"
                  height="1.25rem"
                  type={formData.showClientSecret ? 'eye' : 'eyeOff'}
                />
              </InputButton>
            </Flex>
          </FormGroup>
        </>
      }
    />
  );
};

RegistrationModal.propTypes = {
  state: PropTypes.shape({
    setActiveModal: PropTypes.func.isRequired,
  }).isRequired
};

export default RegistrationModal;